import { useState, useMemo, useCallback } from "react";

import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";
import { translate } from "app/filters/i18nFilter";
import GenericWidgetTable from "app/pages/website-analysis/traffic-sources/search/overview/components/GenericWidgetTable/GenericWidgetTable";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import EnhancedSearchTableTop from "./components/EnhancedSearchTableTop/EnhancedSearchTableTop";
import { getTrafficSourcesTableColumns, getTrafficSourcesTableColumnsForPicker } from "./tableColumns";
import type { FilterChangeEvent, TableSorter } from "./types";
import { useTrafficSourcesData, useTableState, useExcelDownloadUrl } from "./useTrafficSourcesData";

interface IBetaTrafficSourcesTableProps {
    handleOpenQualificationSideBar?: (row: { Domain: string; Favicon: string }) => void;
}

const BetaTrafficSourcesTable = ({ handleOpenQualificationSideBar }: IBetaTrafficSourcesTableProps) => {
    const { initialSortConfig, initialVisibleColumns, initialCategoryId, initialSourceId } = useTableState();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(initialCategoryId);
    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(initialSourceId);
    const [sortConfig, setSortConfig] = useState(initialSortConfig);
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleColumns, setVisibleColumns] = useState(initialVisibleColumns);

    const { data, totalCount, isError, categories, sourceTypes, isLoading } = useTrafficSourcesData({
        searchTerm,
        selectedCategoryId,
        selectedSourceId,
        currentPage,
        sortConfig,
    });

    const excelLink = useExcelDownloadUrl({
        searchTerm,
        selectedCategoryId,
        selectedSourceId,
        currentPage,
        sortConfig,
    });

    const onSort = (sorter: TableSorter) => {
        const columnKey = sorter.columnKey || "share";
        const ascending = sorter.order === null ? true : sorter.order === "ascend";
        setSortConfig({ key: columnKey, ascending });
        setCurrentPage(1);
    };

    const onSearch = (value: string) => {
        allTrackers.trackEvent("Search Bar", "click", `Table/${value}`);
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const onClearAll = () => {
        setSearchTerm("");
        setSelectedSourceId(null);
        setSelectedCategoryId(null);
        proNavigator.updateQueryParams(
            {
                tableCategory: null,
                search: null,
                sourceType: null,
            },
            { reload: false },
        );

        allTrackers.trackEvent("Button", "click", "Table/Clear all");
    };

    const onColumnToggle = useCallback(
        (key: string) => {
            const isCurrentlyVisible = visibleColumns.has(key);
            allTrackers.trackEvent("Drop Down", isCurrentlyVisible ? "remove" : "add", "Table Columns/Change");
            setVisibleColumns((prev) => {
                const newSet = new Set(prev);
                isCurrentlyVisible ? newSet.delete(key) : newSet.add(key);
                return newSet;
            });
        },
        [visibleColumns],
    );

    const onPaginationChange = (_page: number, _pageSize: number, nextPage: number) => {
        setCurrentPage(nextPage);
    };

    const onFilterChange = (filters: FilterChangeEvent) => {
        const { sourceType, category, search } = filters;
        if (sourceType !== undefined) {
            setSelectedSourceId(sourceType || null);
        }
        if (category !== undefined) {
            setSelectedCategoryId(category || null);
        }
        if (search !== undefined) {
            setSearchTerm(search || "");
        }

        if (sourceType !== undefined || category !== undefined || search !== undefined) {
            setCurrentPage(1);
        }
    };

    const columnConfig = useMemo(
        () =>
            getTrafficSourcesTableColumns(
                totalCount,
                sortConfig,
                visibleColumns,
                handleOpenQualificationSideBar,
                currentPage,
            ),
        [totalCount, sortConfig, visibleColumns, handleOpenQualificationSideBar, currentPage],
    );

    return (
        <GenericWidgetTable
            data-automation="traffic-sources-table"
            title={translate("analysis.source.referrals.table.desktop.title")}
            tooltip={translate("analysis.source.referrals.table.desktop.title.tooltip")}
            TableTop={
                <EnhancedSearchTableTop
                    onChange={onSearch}
                    onFilterChange={onFilterChange}
                    onClearAll={onClearAll}
                    selectedCategory={selectedCategoryId !== "no-category" ? selectedCategoryId : ""}
                    selectedSourceType={selectedSourceId || ""}
                    searchTerm={searchTerm}
                    allCategories={categories}
                    allSourceTypes={sourceTypes}
                    tableColumns={getTrafficSourcesTableColumnsForPicker(visibleColumns)}
                    onClickToggleColumns={onColumnToggle}
                    excelLink={excelLink}
                    totalCount={totalCount}
                />
            }
            isError={isError}
            tableProps={{
                data,
                columnConfig: columnConfig as any,
                isLoading,
                bordersConfig: { body: false, groupBorder: true, header: true },
                theme: "gray",
                rowHoverable: true,
                onSort,
                pagination: {
                    current: currentPage,
                    pageSize: 100,
                    total: totalCount,
                    onChange: onPaginationChange,
                },
                loaderRowCount: 104,
                expandable: {
                    expandIconColumnIndex: 1,
                },
            }}
        />
    );
};

export default QualificationSideBarHOC(BetaTrafficSourcesTable);
